/**
 * CasesPage (PAC-008/009).
 *
 * Role-scoped case list (GET /queues/{role}). A role selector drives the RBAC
 * path segment; the default `staff_admin` returns the full tenant case set.
 * Each row opens the case page. Data wiring (useCases) is unchanged.
 */
import { useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCases } from '../hooks/useCases';
import { STAFF_ROLES } from '../api/types';
import type { Case, Role } from '../api/types';
import { colors } from '../theme';
import { PageHeader } from '../components/PageHeader';
import { RoleImpactStrip } from '../components/PlatformImpactStrip';
import { Card } from '../components/Card';
import { DataTable, type Column } from '../components/DataTable';
import { StatusBadge } from '../components/StatusBadge';
import { PriorityPill, tierForScore } from '../components/PriorityPill';
import { RoleBadge, roleLabel } from '../components/RoleBadge';
import { LoadingIndicator } from '../components/LoadingIndicator';

export function CasesPage() {
  const navigate = useNavigate();
  const [role, setRole] = useState<Role>('staff_admin');
  const { cases, isLoading, error, refresh } = useCases(role);

  const columns: Column<Case>[] = [
    {
      key: 'priority',
      header: 'Priority',
      width: 110,
      render: (c) => <PriorityPill tier={tierForScore(c.priority_score)} />,
    },
    { key: 'case', header: 'Case', render: (c) => <span style={caseLink}>{c.id}</span> },
    {
      key: 'stage',
      header: 'Stage',
      render: (c) => <span style={cellMuted}>{c.stage.replace(/_/g, ' ')}</span>,
    },
    { key: 'status', header: 'Status', render: (c) => <StatusBadge value={c.status} /> },
    {
      key: 'assignee',
      header: 'Assignee',
      render: (c) => c.assigned_user_id ?? <span style={cellMuted}>Unassigned</span>,
    },
    {
      key: 'employer',
      header: 'Employer',
      render: (c) => c.employer_id ?? <span style={cellMuted}>—</span>,
    },
    { key: 'open', header: '', align: 'right', render: () => <span style={openBtn}>Open →</span> },
  ];

  return (
    <>
      <PageHeader
        title="Cases"
        subtitle="Tenant-scoped enrollment cases, ranked by priority. Select a role queue to scope the list."
        actions={<RoleBadge role={role} />}
      />
      <RoleImpactStrip />
      <Card
        title={`${roleLabel(role)} cases`}
        actions={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as Role)}
              aria-label="Role queue"
              style={select}
            >
              {STAFF_ROLES.map((r) => (
                <option key={r} value={r}>
                  {roleLabel(r)}
                </option>
              ))}
            </select>
            <button type="button" className="btn btn-secondary btn-sm" onClick={refresh} disabled={isLoading}>
              Refresh
            </button>
          </div>
        }
        padded={false}
      >
        {isLoading && (
          <div style={{ padding: 18 }}>
            <LoadingIndicator text="Loading cases…" />
          </div>
        )}
        {error && (
          <p style={errorText} role="alert">
            {error.message}
          </p>
        )}
        {!isLoading && !error && (
          <DataTable
            columns={columns}
            rows={cases}
            rowKey={(c) => c.id}
            onRowClick={(c) => navigate(`/cases/${c.id}`)}
            emptyTitle="No cases for this role queue"
          />
        )}
      </Card>
    </>
  );
}

const select: CSSProperties = {
  padding: '5px 10px',
  border: `1px solid ${colors.border}`,
  borderRadius: 6,
  fontSize: '0.86rem',
  background: '#FFFFFF',
  fontFamily: 'inherit',
};
const caseLink: CSSProperties = { color: colors.action, fontWeight: 600 };
const cellMuted: CSSProperties = { color: colors.textSecondary, fontSize: '0.86rem' };
const openBtn: CSSProperties = { color: colors.action, fontWeight: 600, fontSize: '0.84rem' };
const errorText: CSSProperties = { color: colors.danger, fontSize: '0.9rem', padding: 18 };
